import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Users, FileText, ChartBar, ArrowLeft, EnvelopeSimple, UserPlus, SignIn } from '@phosphor-icons/react';
import { toast } from 'sonner';
import axios from 'axios';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend } from 'recharts';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Admin = ({ onLogout }) => {
  const [stats, setStats] = useState(null);
  const [weeklyActivity, setWeeklyActivity] = useState([]);
  const [loading, setLoading] = useState(true);
  const [password, setPassword] = useState('');

  useEffect(() => {
    const storedPassword = sessionStorage.getItem('admin_password');
    if (storedPassword) {
      setPassword(storedPassword);
      fetchStats(storedPassword);
      fetchWeeklyActivity(storedPassword);
    } else {
      setLoading(false);
    }
  }, []);

  const fetchStats = async (pwd) => {
    try {
      const response = await axios.get(`${API}/admin/stats?password=${pwd}`);
      setStats(response.data);
    } catch (error) {
      toast.error('Failed to load stats');
      sessionStorage.removeItem('admin_password');
    } finally {
      setLoading(false);
    }
  };

  const fetchWeeklyActivity = async (pwd) => {
    try {
      const response = await axios.get(`${API}/admin/weekly-activity?password=${pwd}`);
      setWeeklyActivity(response.data.weekly_activity || []);
    } catch (error) {
      console.error('Failed to load weekly activity');
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem('admin_password');
    onLogout();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-pulse text-muted-foreground">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md mx-auto space-y-4"
      >
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold">Admin Dashboard</h1>
            <p className="text-xs text-muted-foreground">Analytics Overview</p>
          </div>
          <Button variant="outline" size="sm" onClick={handleLogout}>
            <ArrowLeft size={16} className="mr-1" />
            Exit
          </Button>
        </div>

        {/* Stats Grid */}
        {stats && (
          <>
            <div className="grid grid-cols-2 gap-3">
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 mb-1">
                    <Users size={18} className="text-primary" weight="bold" />
                    <span className="text-[10px] text-muted-foreground uppercase tracking-wide">Total Users</span>
                  </div>
                  <p className="text-2xl font-bold">{stats.total_users}</p>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 mb-1">
                    <FileText size={18} className="text-primary" weight="bold" />
                    <span className="text-[10px] text-muted-foreground uppercase tracking-wide">Statements</span>
                  </div>
                  <p className="text-2xl font-bold">{stats.total_statements}</p>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 mb-1">
                    <ChartBar size={18} className="text-primary" weight="bold" />
                    <span className="text-[10px] text-muted-foreground uppercase tracking-wide">Mode Statements</span>
                  </div>
                  <p className="text-2xl font-bold">{stats.mode_statements_per_user}</p>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 mb-1">
                    <ChartBar size={18} className="text-primary" weight="bold" />
                    <span className="text-[10px] text-muted-foreground uppercase tracking-wide">Mode Logins</span>
                  </div>
                  <p className="text-2xl font-bold">{stats.mode_logins_per_user}</p>
                </CardContent>
              </Card>
            </div>

            {/* Weekly Activity Chart */}
            <Card data-testid="weekly-activity-chart">
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2 text-sm">
                  <ChartBar size={18} className="text-primary" weight="bold" />
                  Weekly Activity (Last 5 Weeks)
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-0">
                {weeklyActivity.length > 0 ? (
                  <ResponsiveContainer width="100%" height={200}>
                    <BarChart data={weeklyActivity} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                      <XAxis 
                        dataKey="week" 
                        tick={{ fontSize: 10 }} 
                        tickLine={false}
                        axisLine={false}
                      />
                      <YAxis 
                        tick={{ fontSize: 10 }} 
                        tickLine={false}
                        axisLine={false}
                        allowDecimals={false}
                      />
                      <Tooltip 
                        contentStyle={{ 
                          fontSize: '12px', 
                          borderRadius: '8px',
                          border: '1px solid #e5e7eb'
                        }}
                      />
                      <Legend 
                        wrapperStyle={{ fontSize: '11px' }}
                        iconSize={10}
                      />
                      <Bar 
                        dataKey="logins" 
                        name="Logins" 
                        fill="#3b82f6" 
                        radius={[4, 4, 0, 0]}
                      />
                      <Bar 
                        dataKey="signups" 
                        name="Signups" 
                        fill="#22c55e" 
                        radius={[4, 4, 0, 0]}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-[200px] flex items-center justify-center text-sm text-muted-foreground">
                    No activity data yet
                  </div>
                )}
                <div className="flex justify-center gap-4 mt-2">
                  <div className="flex items-center gap-1.5">
                    <SignIn size={14} className="text-blue-500" />
                    <span className="text-xs text-muted-foreground">Logins</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <UserPlus size={14} className="text-green-500" />
                    <span className="text-xs text-muted-foreground">Signups</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Registered Emails */}
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-3">
                  <EnvelopeSimple size={18} className="text-primary" weight="bold" />
                  <span className="text-sm font-semibold">Registered Users</span>
                </div>
                <div className="space-y-1 max-h-48 overflow-y-auto">
                  {stats.registered_emails && stats.registered_emails.length > 0 ? (
                    stats.registered_emails.map((email, index) => (
                      <div key={index} className="text-xs text-muted-foreground py-1 px-2 bg-muted/50 rounded">
                        {email}
                      </div>
                    ))
                  ) : (
                    <p className="text-xs text-muted-foreground">No registered users yet</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </>
        )}

        <p className="text-[10px] text-center text-muted-foreground pt-4">
          Data refreshed on page load
        </p>
      </motion.div>
    </div>
  );
};

export default Admin;
